import type { PlaybackMode } from "@recital-master/shared";

type PlaybackQueueOwner = {
  dancerIds?: string[];
  mode: PlaybackMode;
  scopeId: string;
  scopeKind: "performance" | "recital";
};

function framed(values: readonly string[]): string {
  return values.map((value) => `${value.length}:${value}`).join("|");
}

function stableHash(values: readonly string[]): string {
  let hash = 2166136261;
  for (const value of values) {
    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    hash ^= 0xff;
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

/**
 * Identifies the logical owner of one playback queue independently of its
 * contents. A My Dancer queue belongs to its selected dancer set, so changing
 * dancers is a new playback activation even when both filters happen to
 * produce the same ordered dances.
 */
export function playbackQueueOwnerKey({
  dancerIds = [],
  mode,
  scopeId,
  scopeKind
}: PlaybackQueueOwner): string {
  const normalizedDancerIds =
    mode === "my-dancer"
      ? [...new Set(dancerIds.filter(Boolean))].sort()
      : [];
  return framed([
    scopeKind,
    scopeId,
    mode,
    String(normalizedDancerIds.length),
    ...normalizedDancerIds
  ]);
}

/**
 * Receiver-facing identity combines logical ownership with queue contents.
 * The compact hash keeps Cast queue identifiers bounded while the owner key
 * prevents one saved dancer's receiver state from attaching to another's.
 */
export function remotePlaybackQueueId(
  ownerKey: string,
  danceIds: readonly string[]
): string {
  return `queue:${danceIds.length}:${stableHash([ownerKey, ...danceIds])}`;
}

/**
 * Expo Router may reuse the same dynamic watch screen when only query
 * parameters change. Key the screen by queue intent (not the live dance
 * parameter) so switching saved dancers starts with clean route-local state,
 * while ordinary dance changes remain inside one continuous player.
 */
export function watchQueueInstanceKey({
  dancerId,
  mode,
  scopeId,
  scopeKind
}: {
  dancerId?: string;
  mode: PlaybackMode;
  scopeId: string;
  scopeKind: "performance" | "recital";
}): string {
  return framed([
    scopeKind,
    scopeId,
    mode,
    dancerId ? "one-dancer" : "all-saved-dancers",
    dancerId ?? ""
  ]);
}
